import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { 
  Building2, 
  FolderOpen, 
  File, 
  ExternalLink, 
  LogOut, 
  Loader2, 
  ChevronRight, 
  ChevronDown,
  Eye,
  Settings,
  User,
  AlertCircle,
  ArrowLeft,
  Copy,
  Check,
  QrCode,
  X,
  Menu
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

// Get base URL from browser or environment
const getBaseUrl = () => {
  return window.location.origin;
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolderContents, setCurrentFolderContents] = useState([]);
  const [currentFolderPath, setCurrentFolderPath] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingFolders, setLoadingFolders] = useState(new Set());
  const [selectedHub, setSelectedHub] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [apsConfigured, setApsConfigured] = useState(false);
  const [checkingApsConfig, setCheckingApsConfig] = useState(true);
  const [modelViews, setModelViews] = useState([]);
  
  const [generatingUrl, setGeneratingUrl] = useState(false);
  const [copiedUrn, setCopiedUrn] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    checkApsConfiguration();
    loadModelViews();
  }, []);

  const checkApsConfiguration = async () => {
    try {
      const response = await axios.get('/settings/aps-credentials');
      setApsConfigured(response.data.configured);
      if (response.data.configured) {
        loadHubs();
      }
    } catch (err) {
      console.error('Failed to check APS configuration:', err);
    } finally {
      setCheckingApsConfig(false);
    }
  };

  const loadModelViews = async () => {
    try {
      const response = await axios.get('/translation/jobs');
      setModelViews(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to load model views:', err);
    }
  };

  const loadHubs = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/projects/hubs');
      setHubs(response.data.hubs || []);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to load hubs';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async (hubId) => {
    try {
      const response = await axios.get(`/projects/hubs/${hubId}/projects`);
      setProjects(response.data.projects || []);
      setSelectedHub(hubId);
      toggleNodeExpansion(`hub-${hubId}`);
    } catch (err) {
      toast.error('Failed to load projects');
    }
  };

  const loadFolders = async (hubId, projectId) => {
    try {
      const response = await axios.get(`/projects/${hubId}/${projectId}/folders`);
      setFolders(response.data.folders || []);
      setSelectedProject(projectId);
      toggleNodeExpansion(`project-${projectId}`);
    } catch (err) {
      toast.error('Failed to load folders');
    }
  };

  const loadFolderContents = async (projectId, folderId, folderName = '', isRoot = false) => {
    setLoadingFolders(prev => new Set([...prev, folderId]));
    
    try {
      const response = await axios.get(`/projects/${projectId}/folders/${folderId}/contents`);
      const folderContentsData = response.data.contents || [];
      
      const folders = folderContentsData.filter(item => item.type === 'folders');
      const files = folderContentsData.filter(item => item.type === 'items');
      
      setCurrentFolderContents([...folders, ...files]);
      
      if (isRoot) {
        setCurrentFolderPath([{ id: folderId, name: folderName }]);
      } else {
        setCurrentFolderPath(prev => [...prev, { id: folderId, name: folderName }]);
      }
      
    } catch (err) {
      toast.error('Failed to load folder contents');
    } finally {
      setLoadingFolders(prev => {
        const newSet = new Set(prev);
        newSet.delete(folderId);
        return newSet;
      });
    }
  };

  const navigateUp = () => {
    if (currentFolderPath.length > 1) {
      const newPath = currentFolderPath.slice(0, -1);
      const parentFolder = newPath[newPath.length - 1];
      setCurrentFolderPath(newPath);
      loadFolderContentsForNavigation(selectedProject, parentFolder.id);
    } else {
      setCurrentFolderPath([]);
      setCurrentFolderContents([]);
    }
  };

  const loadFolderContentsForNavigation = async (projectId, folderId) => {
    setLoadingFolders(prev => new Set([...prev, folderId]));
    
    try {
      const response = await axios.get(`/projects/${projectId}/folders/${folderId}/contents`);
      const folderContentsData = response.data.contents || [];
      
      const folders = folderContentsData.filter(item => item.type === 'folders');
      const files = folderContentsData.filter(item => item.type === 'items');
      
      setCurrentFolderContents([...folders, ...files]);
      
    } catch (err) {
      toast.error('Failed to load folder contents');
    } finally {
      setLoadingFolders(prev => {
        const newSet = new Set(prev);
        newSet.delete(folderId);
        return newSet;
      });
    }
  };

  const toggleNodeExpansion = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const generatePublicUrl = async (item) => {
    if (item.type !== 'items') {
      toast.error('Only model files can be viewed');
      return;
    }

    const fileName = item.attributes?.name || item.attributes?.displayName || 'unknown';
    const supportedFormats = ['.rvt', '.ifc', '.dwg', '.pdf', '.nwd', '.dwf'];
    const hasExtension = fileName.includes('.');
    const isSupported = !hasExtension || supportedFormats.some(format => 
      fileName.toLowerCase().endsWith(format)
    );

    if (!isSupported) {
      toast.error(`Unsupported file format. Supported: ${supportedFormats.join(', ')}`);
      return;
    }

    setGeneratingUrl(true);
    
    const requestData = {
      projectId: selectedProject,
      itemId: item.id,
      versionId: item.relationships?.tip?.data?.id || item.id,
      fileName: fileName
    };

    try {
      const response = await axios.post('/files/generate-public-url', requestData);

      if (response.data.success) {
        const viewId = response.data.viewId;
        const baseUrl = getBaseUrl();
        const publicUrl = `${baseUrl}${response.data.publicUrl}`;
        
        toast.success(response.data.message || 'Model ready to view!');
        
        // Update selected item with view info
        setSelectedItem(prev => ({
          ...prev,
          viewId: viewId,
          publicUrl: publicUrl
        }));
        
        loadModelViews();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to generate public URL';
      toast.error(errorMsg);
    } finally {
      setGeneratingUrl(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.info('Logged out successfully');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
      case 'ready':
        return <Badge className="badge-success">Ready</Badge>;
      case 'inprogress':
      case 'pending':
        return <Badge className="badge-processing">Processing</Badge>;
      case 'failed':
        return <Badge className="badge-failed">Failed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedUrn(true);
    setTimeout(() => setCopiedUrn(false), 2000);
    toast.success('Copied to clipboard');
  };

  // Get folder/file name - prefer 'name' over 'displayName'
  const getItemName = (item) => {
    return item.attributes?.name || item.attributes?.displayName || 'Unknown';
  };

  if (checkingApsConfig) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b border-slate-200 p-4 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between">
          <Button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            variant="ghost" 
            size="sm"
            className="btn-ghost text-xs"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <h1 className="technical-header text-lg flex-1 text-center">DASHBOARD</h1>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate('/settings')} 
              variant="ghost" 
              size="sm"
              className="btn-ghost text-xs"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button 
              onClick={handleLogout} 
              variant="ghost" 
              size="sm"
              className="btn-ghost text-xs"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Overlay - click to close drawer */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/30 z-30 top-16"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Add padding for mobile header */}
      <div className="md:hidden h-16"></div>

      {/* Left Sidebar - File Explorer (Drawer on mobile) */}
      <div className={`sidebar md:relative fixed md:static inset-0 md:inset-auto top-16 left-0 right-auto md:right-auto w-80 md:w-auto h-auto md:h-auto z-40 md:z-auto transition-transform duration-300 md:translate-x-0 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-4 border-b border-slate-200">
          <div className="hidden md:flex items-center justify-between mb-4">
            <h1 className="technical-header text-lg">PROJECT EXPLORER</h1>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => navigate('/settings')} 
                variant="ghost" 
                size="sm"
                data-testid="settings-button"
                className="btn-ghost text-xs"
              >
                <Settings className="w-4 h-4 mr-1" />
                SETTINGS
              </Button>
              <Button 
                onClick={handleLogout} 
                variant="ghost" 
                size="sm"
                data-testid="logout-button"
                className="btn-ghost text-xs"
              >
                <LogOut className="w-4 h-4 mr-1" />
                LOGOUT
              </Button>
            </div>
          </div>

          {user && (
            <div className="mb-4 p-3 bg-slate-100 rounded-sm border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-slate-600" />
                <span className="technical-header text-sm">{user.full_name || 'User'}</span>
              </div>
              <div className="mono text-xs text-slate-500">
                {user.email}
              </div>
            </div>
          )}
          
          {!apsConfigured && (
            <Alert className="mb-4 border-orange-200 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                <div className="mb-2">APS credentials not configured.</div>
                <Button 
                  size="sm" 
                  onClick={() => navigate('/settings')}
                  className="btn-primary text-xs"
                  data-testid="setup-credentials-button"
                >
                  Setup Credentials
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        {apsConfigured && (
          <div className="file-tree overflow-y-auto" data-testid="file-explorer">
            {loading ? (
              <div className="p-4 text-center">
                <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
                <p className="technical-subtext text-sm">Loading...</p>
              </div>
            ) : (
              <div className="space-y-1">
                {currentFolderPath.length > 0 ? (
                  <div>
                    {/* Breadcrumb header */}
                    <div className="p-3 border-b border-slate-200 bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <FolderOpen className="w-4 h-4 text-orange-500" />
                        <span className="technical-header text-sm">
                          {currentFolderPath[currentFolderPath.length - 1]?.name || 'Current Folder'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-600 flex-wrap">
                        {currentFolderPath.map((folder, index) => (
                          <span key={folder.id} className="flex items-center gap-1">
                            {index > 0 && <ChevronRight className="w-3 h-3" />}
                            <span className="mono truncate max-w-24">{folder.name}</span>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* UP navigation */}
                    <div 
                      className="tree-node border-b border-slate-100"
                      onClick={navigateUp}
                      data-testid="navigate-up-button"
                    >
                      <ArrowLeft className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">
                        UP {currentFolderPath.length > 1 ? `(${currentFolderPath[currentFolderPath.length - 2]?.name})` : '(Project Root)'}
                      </span>
                    </div>

                    {/* Current folder contents */}
                    <div className="pt-2">
                      {currentFolderContents.length === 0 ? (
                        <div className="p-4 text-center text-slate-500 text-sm">
                          This folder is empty
                        </div>
                      ) : (
                        currentFolderContents.map((item) => (
                          <div key={item.id}>
                            {item.type === 'folders' ? (
                              <div 
                                className={`tree-node ${selectedItem?.id === item.id ? 'bg-orange-100 border-orange-200' : ''}`}
                                onClick={() => {
                                  setSelectedItem(item);
                                  loadFolderContents(selectedProject, item.id, getItemName(item), false);
                                }}
                                data-testid={`folder-${item.id}`}
                              >
                                {loadingFolders.has(item.id) ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                                <FolderOpen className="w-4 h-4 text-yellow-600" />
                                <span className="text-sm truncate">
                                  {getItemName(item)}
                                </span>
                              </div>
                            ) : (
                              <div 
                                className={`tree-node group ${selectedItem?.id === item.id ? 'bg-orange-100 border-orange-200' : ''}`}
                                onClick={() => setSelectedItem(item)}
                              >
                                <File className="w-4 h-4 text-green-600" />
                                <span className="text-sm truncate flex-1">
                                  {getItemName(item)}
                                </span>
                                {item.type === 'items' && (
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      generatePublicUrl(item);
                                    }}
                                    disabled={generatingUrl}
                                    data-testid={`generate-url-${item.id}`}
                                    className="opacity-0 group-hover:opacity-100 btn-primary text-xs px-2 py-1"
                                  >
                                    {generatingUrl ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <>
                                        <ExternalLink className="w-3 h-3 mr-1" />
                                        VIEW
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    {hubs.map((hub) => (
                      <div key={hub.id}>
                        <div 
                          className="tree-node"
                          onClick={() => loadProjects(hub.id)}
                          data-testid={`hub-${hub.id}`}
                        >
                          {expandedNodes.has(`hub-${hub.id}`) ? 
                            <ChevronDown className="w-4 h-4" /> : 
                            <ChevronRight className="w-4 h-4" />
                          }
                          <Building2 className="w-4 h-4 text-slate-600" />
                          <span className="text-sm font-medium truncate">
                            {hub.attributes?.name}
                          </span>
                        </div>
                        
                        {expandedNodes.has(`hub-${hub.id}`) && selectedHub === hub.id && (
                          <div className="tree-indent-1">
                            {projects.map((project) => (
                              <div key={project.id}>
                                <div 
                                  className="tree-node"
                                  onClick={() => loadFolders(hub.id, project.id)}
                                  data-testid={`project-${project.id}`}
                                >
                                  {expandedNodes.has(`project-${project.id}`) ? 
                                    <ChevronDown className="w-4 h-4" /> : 
                                    <ChevronRight className="w-4 h-4" />
                                  }
                                  <FolderOpen className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm truncate">
                                    {project.attributes?.name}
                                  </span>
                                </div>
                                
                                {expandedNodes.has(`project-${project.id}`) && selectedProject === project.id && (
                                  <div className="tree-indent-2">
                                    {folders.map((folder) => (
                                      <div 
                                        key={folder.id}
                                        className={`tree-node ${selectedItem?.id === folder.id ? 'bg-orange-100 border-orange-200' : ''}`}
                                        onClick={() => {
                                          setSelectedItem(folder);
                                          loadFolderContents(project.id, folder.id, getItemName(folder), true);
                                        }}
                                        data-testid={`root-folder-${folder.id}`}
                                      >
                                        {loadingFolders.has(folder.id) ? (
                                          <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4" />
                                        )}
                                        <FolderOpen className="w-4 h-4 text-yellow-600" />
                                        <span className="text-sm truncate">
                                          {getItemName(folder)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area - Hidden on mobile, marketing/guidance only */}
      <div className="main-content hidden md:block">
        {/* Model Views History */}
        {modelViews.length > 0 && (
          <div className="mb-6">
            <h2 className="technical-header text-xl mb-4">YOUR MODEL VIEWS</h2>
            <div className="grid gap-3" data-testid="model-views-history">
              {modelViews.slice(0, 5).map((view) => (
                <Card key={view.jobId || view.viewId} className="card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-sm text-slate-800 mb-1">{view.fileName}</div>
                        <div className="text-xs text-slate-500 mono">{view.createdAt}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(view.status)}
                        {(view.status === 'success' || view.status === 'ready') && view.publicUrl && (
                          <Button 
                            size="sm" 
                            onClick={() => window.open(window.location.origin + view.publicUrl, '_blank')}
                            data-testid={`view-model-${view.jobId || view.viewId}`}
                            className="btn-ghost text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            VIEW
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Welcome/Instructions */}
        {modelViews.length === 0 && (
          <div className="max-w-2xl">
            <Card className="card">
              <CardHeader>
                <CardTitle className="technical-header text-2xl mb-2">
                  APS MODEL VIEWER DASHBOARD
                </CardTitle>
                <CardDescription className="technical-subtext text-base leading-relaxed">
                  {apsConfigured ? 
                    "Navigate through your ACC/BIM 360 projects in the file explorer. Select published model files and generate public, shareable viewer URLs." :
                    "Welcome! First, configure your APS credentials in Settings to start browsing your ACC/BIM 360 projects."
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-sm text-blue-800">
                    <strong>Note:</strong> ACC automatically creates viewable derivatives for <strong>published</strong> models. 
                    Make sure your model has been published in ACC before generating a viewer URL.
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">1</div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">
                        {apsConfigured ? "Browse Projects" : "Setup APS Credentials"}
                      </h4>
                      <p className="technical-subtext text-sm">
                        {apsConfigured ? 
                          "Expand hubs and projects in the left panel to navigate your files." :
                          "Click Settings to add your Autodesk Platform Services Client ID and Secret."
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">2</div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">Generate Viewer URLs</h4>
                      <p className="technical-subtext text-sm">Click VIEW on published model files (.rvt, .ifc, .dwg) to generate instant viewer links.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">3</div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">Share & View</h4>
                      <p className="technical-subtext text-sm">Share the generated URLs with anyone - uses ACC's built-in derivatives, no translation needed!</p>
                    </div>
                  </div>
                </div>
                
                {!apsConfigured && (
                  <div className="mt-6">
                    <Button 
                      onClick={() => navigate('/settings')} 
                      className="btn-primary"
                      data-testid="setup-now-button"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      SETUP APS CREDENTIALS
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Right Panel - File/Folder Details */}
      {selectedItem && (
        <>
          {/* Mobile overlay to close drawer */}
          <div 
            className="md:hidden fixed inset-0 bg-black/30 z-40"
            onClick={() => setSelectedItem(null)}
          />
          {/* Details drawer */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-lg shadow-2xl z-50 max-h-[70vh] flex flex-col animate-in slide-in-from-bottom">
            <div className="p-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h2 className="technical-header text-lg">DETAILS</h2>
                <Button 
                  onClick={() => setSelectedItem(null)} 
                  variant="ghost" 
                  size="sm"
                  className="btn-ghost text-xs"
                >
                  ✕
                </Button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {/* Item Icon and Name */}
                <div className="flex items-center gap-3 mb-4">
                  {selectedItem.type === 'folders' ? (
                    <FolderOpen className="w-8 h-8 text-yellow-600" />
                  ) : (
                    <File className="w-8 h-8 text-green-600" />
                  )}
                  <div>
                    <h3 className="font-semibold text-slate-800 text-sm break-words">
                      {getItemName(selectedItem)}
                    </h3>
                    <p className="text-xs text-slate-500 mono">
                      {selectedItem.type === 'folders' ? 'Folder' : 'File'}
                    </p>
                  </div>
                </div>

                {/* Item Details - Only show non-sensitive info */}
                <div className="space-y-3">
                  {selectedItem.publicUrl && (
                    <div>
                      <label className="technical-header text-xs text-slate-600 block mb-1">PUBLIC VIEW URL</label>
                      <div className="relative">
                        <p className="mono text-xs text-blue-700 break-all bg-blue-50 p-2 rounded pr-16">
                          {selectedItem.publicUrl}
                        </p>
                        <div className="absolute right-2 top-1.5 flex items-center gap-1">
                          <button 
                            onClick={() => {
                              setQrUrl(selectedItem.publicUrl);
                              setShowQrModal(true);
                            }}
                            className="text-slate-400 hover:text-slate-600 p-1"
                            title="Show QR Code"
                          >
                            <QrCode className="w-3.5 h-3.5" />
                          </button>
                          <button 
                            onClick={() => copyToClipboard(selectedItem.publicUrl)}
                            className="text-slate-400 hover:text-slate-600 p-1"
                            title="Copy URL"
                          >
                            {copiedUrn ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedItem.attributes?.createTime && (
                    <div>
                      <label className="technical-header text-xs text-slate-600 block mb-1">CREATED</label>
                      <p className="mono text-xs text-slate-800">
                        {new Date(selectedItem.attributes.createTime).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {selectedItem.attributes?.lastModifiedTime && (
                    <div>
                      <label className="technical-header text-xs text-slate-600 block mb-1">MODIFIED</label>
                      <p className="mono text-xs text-slate-800">
                        {new Date(selectedItem.attributes.lastModifiedTime).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {selectedItem.type === 'items' && (
                  <div className="pt-4 border-t border-slate-200 space-y-2">
                    <Button
                      onClick={() => generatePublicUrl(selectedItem)}
                      disabled={generatingUrl}
                      className="btn-primary w-full text-xs"
                      data-testid="details-generate-url"
                    >
                      {generatingUrl ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                          GENERATING...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-3 h-3 mr-2" />
                          GENERATE VIEW URL
                        </>
                      )}
                    </Button>
                    
                    {selectedItem.publicUrl && (
                      <>
                        <Button
                          onClick={() => window.open(selectedItem.publicUrl, '_blank')}
                          className="btn-secondary w-full text-xs"
                        >
                          <Eye className="w-3 h-3 mr-2" />
                          OPEN IN VIEWER
                        </Button>
                        <Button
                          onClick={() => {
                            setQrUrl(selectedItem.publicUrl);
                            setShowQrModal(true);
                          }}
                          className="btn-ghost w-full text-xs"
                        >
                          <QrCode className="w-3 h-3 mr-2" />
                          SHOW QR CODE
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Desktop Details Panel */}
          <div className="hidden md:flex md:w-80 bg-white border-l border-slate-200 flex-col">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h2 className="technical-header text-lg">DETAILS</h2>
                <Button 
                  onClick={() => setSelectedItem(null)} 
                  variant="ghost" 
                  size="sm"
                  className="btn-ghost text-xs"
                >
                  ✕
                </Button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {/* Item Icon and Name */}
                <div className="flex items-center gap-3 mb-4">
                  {selectedItem.type === 'folders' ? (
                    <FolderOpen className="w-8 h-8 text-yellow-600" />
                  ) : (
                    <File className="w-8 h-8 text-green-600" />
                  )}
                  <div>
                    <h3 className="font-semibold text-slate-800 text-sm break-words">
                      {getItemName(selectedItem)}
                    </h3>
                    <p className="text-xs text-slate-500 mono">
                      {selectedItem.type === 'folders' ? 'Folder' : 'File'}
                    </p>
                  </div>
                </div>

                {/* Item Details - Only show non-sensitive info */}
                <div className="space-y-3">
                  {selectedItem.publicUrl && (
                    <div>
                      <label className="technical-header text-xs text-slate-600 block mb-1">PUBLIC VIEW URL</label>
                      <div className="relative">
                        <p className="mono text-xs text-blue-700 break-all bg-blue-50 p-2 rounded pr-16">
                          {selectedItem.publicUrl}
                        </p>
                        <div className="absolute right-2 top-1.5 flex items-center gap-1">
                          <button 
                            onClick={() => {
                              setQrUrl(selectedItem.publicUrl);
                              setShowQrModal(true);
                            }}
                            className="text-slate-400 hover:text-slate-600 p-1"
                            title="Show QR Code"
                          >
                            <QrCode className="w-3.5 h-3.5" />
                          </button>
                          <button 
                            onClick={() => copyToClipboard(selectedItem.publicUrl)}
                            className="text-slate-400 hover:text-slate-600 p-1"
                            title="Copy URL"
                          >
                            {copiedUrn ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedItem.attributes?.createTime && (
                    <div>
                      <label className="technical-header text-xs text-slate-600 block mb-1">CREATED</label>
                      <p className="mono text-xs text-slate-800">
                        {new Date(selectedItem.attributes.createTime).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {selectedItem.attributes?.lastModifiedTime && (
                    <div>
                      <label className="technical-header text-xs text-slate-600 block mb-1">MODIFIED</label>
                      <p className="mono text-xs text-slate-800">
                        {new Date(selectedItem.attributes.lastModifiedTime).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {selectedItem.type === 'items' && (
                  <div className="pt-4 border-t border-slate-200 space-y-2">
                    <Button
                      onClick={() => generatePublicUrl(selectedItem)}
                      disabled={generatingUrl}
                      className="btn-primary w-full text-xs"
                      data-testid="details-generate-url"
                    >
                      {generatingUrl ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                          GENERATING...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-3 h-3 mr-2" />
                          GENERATE VIEW URL
                        </>
                      )}
                    </Button>
                    
                    {selectedItem.publicUrl && (
                      <>
                        <Button
                          onClick={() => window.open(selectedItem.publicUrl, '_blank')}
                          className="btn-secondary w-full text-xs"
                        >
                          <Eye className="w-3 h-3 mr-2" />
                          OPEN IN VIEWER
                        </Button>
                        <Button
                          onClick={() => {
                            setQrUrl(selectedItem.publicUrl);
                            setShowQrModal(true);
                          }}
                          className="btn-ghost w-full text-xs"
                        >
                          <QrCode className="w-3 h-3 mr-2" />
                          SHOW QR CODE
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* QR Code Modal */}
      {showQrModal && qrUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowQrModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="technical-header text-lg">SCAN TO VIEW</h3>
              <button 
                onClick={() => setShowQrModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex justify-center mb-4 p-4 bg-white border border-slate-200 rounded-lg">
              <QRCodeSVG 
                value={qrUrl} 
                size={200}
                level="M"
                includeMargin={true}
              />
            </div>
            
            <div className="text-center mb-4">
              <p className="text-xs text-slate-500 mb-2">Scan this QR code to open the 3D model viewer</p>
              <p className="mono text-xs text-blue-600 break-all bg-slate-50 p-2 rounded">{qrUrl}</p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={() => {
                  copyToClipboard(qrUrl);
                  toast.success('URL copied to clipboard');
                }}
                className="btn-secondary flex-1 text-xs"
              >
                <Copy className="w-3 h-3 mr-2" />
                COPY URL
              </Button>
              <Button 
                onClick={() => setShowQrModal(false)}
                className="btn-primary flex-1 text-xs"
              >
                CLOSE
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Subtle footer branding */}
      <div className="fixed bottom-4 right-4 z-10">
        <p className="text-xs text-slate-400 font-light">
          Engineered by{' '}
          <a 
            href="https://thedevguys.in" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-slate-500 hover:text-orange-500 transition-colors"
          >
            The Dev Guys
          </a>
        </p>
      </div>
    </div>
  );
}
